import { useCallback, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { numberLocale, useLocale, useT, type Translations } from '@/shared/i18n'
import {
  createTxtifyEditingSession,
  runTxtifyToolOperation,
  TXTIFY_ANALYTICS_TOOLS,
} from '@/shared/analytics/tool-operations'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { useCopyToClipboard } from '@/shared/ui/useCopyToClipboard'

type CaseMode = 'lower' | 'upper' | 'title' | 'sentence' | 'capitalize'

interface ModeOption {
  mode: CaseMode
  label: string
  hint: string
}

function buildModes(t: Translations): ModeOption[] {
  const m = t.tools.textCase
  return [
    { mode: 'lower', label: 'lowercase', hint: m.modeLowerHint },
    { mode: 'upper', label: 'UPPERCASE', hint: m.modeUpperHint },
    { mode: 'title', label: 'Title Case', hint: m.modeTitleHint },
    { mode: 'sentence', label: 'Sentence case', hint: m.modeSentenceHint },
    { mode: 'capitalize', label: 'Capitalize', hint: m.modeCapitalizeHint },
  ]
}

function capitalizeWord(word: string): string {
  if (word.length === 0) return word
  const first = word[0]
  if (!first) return word
  return first.toLocaleUpperCase() + word.slice(1).toLocaleLowerCase()
}

function transformText(text: string, mode: CaseMode): string {
  switch (mode) {
    case 'lower':
      return text.toLocaleLowerCase()
    case 'upper':
      return text.toLocaleUpperCase()
    case 'title':
      return text.replace(/\S+/g, (word) => capitalizeWord(word))
    case 'sentence': {
      // Lowercase everything first, then uppercase sentence starts
      const lowercased = text.toLocaleLowerCase()
      return lowercased.replace(/(^|[.!?…]+\s*)(\S)/g, (_match, before, char: string) => {
        return before + char.toLocaleUpperCase()
      })
    }
    case 'capitalize': {
      if (text.length === 0) return text
      const first = text[0]
      if (!first) return text
      return first.toLocaleUpperCase() + text.slice(1)
    }
    default:
      return text
  }
}

export function CaseConverterWorkspace() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [mode, setMode] = useState<CaseMode>('lower')
  const [analyticsSession] = useState(() => (
    createTxtifyEditingSession(TXTIFY_ANALYTICS_TOOLS.textCase)
  ))
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const { copied, copy } = useCopyToClipboard()
  const hasInput = input.length > 0

  const MODES = useMemo(() => buildModes(t), [t])

  const output = useMemo(() => transformText(input, mode), [input, mode])

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const next = event.target.value
      analyticsSession.run(next.length > 0, () => setInput(next))
    },
    [analyticsSession],
  )

  const handleModeChange = useCallback((nextMode: CaseMode) => {
    if (nextMode === mode) return
    if (!hasInput) {
      setMode(nextMode)
      return
    }
    runTxtifyToolOperation(
      TXTIFY_ANALYTICS_TOOLS.textCase,
      () => setMode(nextMode),
    )
  }, [hasInput, mode])

  const handleClear = useCallback(() => {
    analyticsSession.run(false, () => setInput(''))
    inputRef.current?.focus()
  }, [analyticsSession])

  const handleSwap = useCallback(() => {
    setInput(output)
  }, [output])

  const handleCopyOutput = useCallback(() => {
    if (output) {
      copy(output)
    }
  }, [copy, output])

  const wordCount = input.trim() ? input.trim().split(/\s+/).length : 0
  const currentMode = MODES.find((m) => m.mode === mode)

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
        <div className="min-w-0 space-y-3">
          <Card className="overflow-hidden shadow-sm">
            <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-2">
              <div>
                <div className="text-sm font-medium tracking-normal">{t.caseConverter.sourceText}</div>
                <div className="text-xs text-muted-foreground">
                  {input.length.toLocaleString(localeCode)} {t.caseConverter.charsShort}
                </div>
              </div>
              {hasInput && (
                <Button variant="outline" size="sm" onClick={handleClear}>
                  {t.caseConverter.clear}
                </Button>
              )}
            </div>

            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              placeholder={t.caseConverter.inputPlaceholder}
              className="block min-h-[180px] w-full resize-y border-0 bg-card p-4 font-mono text-[15px] leading-7 text-foreground placeholder:text-muted-foreground/55 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35 sm:p-5 lg:min-h-[280px]"
              aria-label={t.caseConverter.inputLabel}
            />
          </Card>

          <div role="group" aria-label={t.caseConverter.modeTitle}>
            <div className="flex flex-wrap gap-2">
              {MODES.map((option) => (
                <Button
                  key={option.mode}
                  variant={mode === option.mode ? 'default' : 'outline'}
                  size="sm"
                  aria-pressed={mode === option.mode}
                  onClick={() => handleModeChange(option.mode)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{currentMode?.hint}</p>
          </div>
        </div>

        <Card className="flex min-w-0 flex-col overflow-hidden shadow-sm">
          <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-2">
            <div>
              <div className="text-sm font-medium tracking-normal">{t.caseConverter.result}</div>
              <div className="text-xs text-muted-foreground">
                {output.length.toLocaleString(localeCode)} {t.caseConverter.charsShort}
              </div>
            </div>
            {hasInput && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={handleSwap}>
                  {t.caseConverter.swap}
                </Button>
                <Button variant="outline" size="sm" onClick={handleCopyOutput}>
                  {copied ? t.caseConverter.copied : t.caseConverter.copy}
                </Button>
              </div>
            )}
          </div>

          <div
            className={cn(
              'min-h-[180px] w-full flex-1 overflow-auto p-4 sm:p-5 lg:min-h-[280px]',
              'whitespace-pre-wrap break-words bg-card font-mono text-[15px] leading-7 text-foreground',
            )}
          >
            {output || (
              <span className="text-muted-foreground/50">{t.caseConverter.outputPlaceholder}</span>
            )}
          </div>
        </Card>
      </div>

      {/* Stats */}
      {hasInput && (
        <Card className="mt-4 shadow-sm">
          <CardContent className="p-4">
            <div className="text-sm font-medium tracking-normal">{t.caseConverter.statsTitle}</div>
            <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4 text-sm">
              <div>
                <span className="text-muted-foreground">{t.caseConverter.statsInput}:</span>{' '}
                <span className="font-medium tabular-nums">{input.length.toLocaleString(localeCode)}</span>{' '}
                <span className="text-muted-foreground">{t.caseConverter.charsShort}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t.caseConverter.statsOutput}:</span>{' '}
                <span className="font-medium tabular-nums">{output.length.toLocaleString(localeCode)}</span>{' '}
                <span className="text-muted-foreground">{t.caseConverter.charsShort}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t.caseConverter.statsWords}:</span>{' '}
                <span className="font-medium tabular-nums">
                  {wordCount.toLocaleString(localeCode)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">{t.caseConverter.statsMode}:</span>{' '}
                <span className="font-medium">{currentMode?.label ?? ''}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  )
}

export function CaseConverterPage() {
  return (
    <ToolPageShell toolKey="textCase">
      <CaseConverterWorkspace />
    </ToolPageShell>
  )
}
